import { useState } from "react";
import { Copy, Check, ExternalLink, Code2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const snippets = (origin) => ({
  simple: `<iframe
  src="${origin}/"
  title="Billy — Bill Reminders"
  style="width:100%; height:900px; border:0; display:block;"
  loading="lazy"
  allow="clipboard-write; camera; fullscreen"
></iframe>`,
  responsive: `<div style="position:relative; width:100%; padding-top:130%; max-height:1200px; min-height:720px; height:85vh;">
  <iframe
    src="${origin}/"
    title="Billy — Bill Reminders"
    style="position:absolute; inset:0; width:100%; height:100%; border:0;"
    loading="lazy"
    allow="clipboard-write; camera; fullscreen"
  ></iframe>
</div>`,
  bookmarklet: `${origin}/`,
});

const CodeBlock = ({ code, testid }) => {
  const [copied, setCopied] = useState(false);
  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Could not copy — please select and copy manually.");
    }
  };
  return (
    <div className="relative" data-testid={testid}>
      <pre className="bg-neutral-900 text-neutral-100 text-xs p-5 overflow-x-auto border-2 border-black whitespace-pre font-mono leading-relaxed">
        {code}
      </pre>
      <button
        data-testid={`${testid}-copy`}
        onClick={doCopy}
        className="absolute top-3 right-3 border-2 border-white bg-white text-black font-bold uppercase tracking-widest text-[10px] px-3 py-2 hover:bg-black hover:text-white transition-colors inline-flex items-center gap-2"
      >
        {copied ? (
          <>
            <Check className="h-3 w-3" /> Copied
          </>
        ) : (
          <>
            <Copy className="h-3 w-3" /> Copy
          </>
        )}
      </button>
    </div>
  );
};

const Step = ({ n, title, children }) => (
  <li className="relative pl-12 pb-6 border-l-2 border-neutral-200 last:border-l-0 last:pb-0">
    <div className="absolute -left-[14px] top-0 h-6 w-6 bg-black text-white flex items-center justify-center font-bold text-xs">
      {n}
    </div>
    <div className="font-display font-extrabold text-lg">{title}</div>
    <div className="text-sm text-neutral-700 mt-2 space-y-2">{children}</div>
  </li>
);

const EmbedPage = () => {
  const origin = BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
  const code = snippets(origin);

  return (
    <div data-testid="embed-page" className="fade-rise">
      <div className="swiss-label mb-3">Embed</div>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-tight">
        Put Billy on your GoDaddy site
      </h1>
      <p className="text-neutral-600 mt-3 max-w-2xl">
        Paste one of the snippets below into a GoDaddy HTML block. The app runs
        inside your page — your visitors (you + staff) can still capture bills,
        log income, and view weekly summaries without leaving your site.
      </p>

      <section className="mt-8 swiss-card">
        <div className="flex items-center gap-2 mb-3">
          <Code2 className="h-4 w-4" />
          <h2 className="font-display font-extrabold text-lg uppercase tracking-widest">
            Option A — Simple (fixed height)
          </h2>
        </div>
        <p className="text-sm text-neutral-600 mb-4">
          Drop this into a GoDaddy HTML section. Adjust{" "}
          <code className="bg-neutral-100 px-1.5 py-0.5 text-xs">height</code>{" "}
          if you want more room.
        </p>
        <CodeBlock code={code.simple} testid="code-simple" />
      </section>

      <section className="mt-6 swiss-card">
        <div className="flex items-center gap-2 mb-3">
          <Code2 className="h-4 w-4" />
          <h2 className="font-display font-extrabold text-lg uppercase tracking-widest">
            Option B — Responsive (recommended)
          </h2>
        </div>
        <p className="text-sm text-neutral-600 mb-4">
          Scales with the visitor's browser height on phones and desktop.
        </p>
        <CodeBlock code={code.responsive} testid="code-responsive" />
      </section>

      <section className="mt-6 swiss-card">
        <div className="flex items-center gap-2 mb-3">
          <ExternalLink className="h-4 w-4" />
          <h2 className="font-display font-extrabold text-lg uppercase tracking-widest">
            Option C — Button / Link
          </h2>
        </div>
        <p className="text-sm text-neutral-600 mb-4">
          If you'd rather open the app in a new tab (best on phones), link to:
        </p>
        <CodeBlock code={code.bookmarklet} testid="code-link" />
      </section>

      <section className="mt-10 swiss-card" data-testid="embed-instructions">
        <div className="swiss-label mb-2">Steps</div>
        <h2 className="font-display font-extrabold text-2xl">
          Add it to GoDaddy (60 seconds)
        </h2>
        <ol className="mt-6 space-y-0">
          <Step n="1" title="Open your GoDaddy site editor">
            <p>
              Log in to GoDaddy → your site → <b>Edit Website</b>. Pick the
              page where you want Billy to live (e.g. an internal "Admin"
              page, or a password-protected one).
            </p>
          </Step>
          <Step n="2" title="Add an HTML section">
            <p>
              Click <b>Add Section</b> (the + icon) → search for{" "}
              <b>HTML</b> → pick <b>Add my own HTML</b>.
            </p>
          </Step>
          <Step n="3" title="Paste the snippet">
            <p>
              Paste the code from <b>Option B</b> above into the HTML box, then
              click <b>Done</b>.
            </p>
          </Step>
          <Step n="4" title="Publish">
            <p>
              Click <b>Publish</b> (top right in GoDaddy) — that's it. Visit
              your page and Billy will be running inside it.
            </p>
          </Step>
          <Step n="5" title="Keep it private">
            <p>
              <b>Recommended:</b> put the embedded page behind a password or
              hidden nav in GoDaddy so only you and staff can see it. The data
              lives in the cloud; anyone who can load the page can use it.
            </p>
          </Step>
        </ol>
      </section>

      <section className="mt-6 swiss-card border-l-4 border-l-amber-500">
        <div className="swiss-label mb-1 text-amber-700">Heads up</div>
        <div className="font-display font-extrabold text-lg">
          Once you deploy the app via Emergent, use the deployed URL above.
        </div>
        <p className="text-sm text-neutral-700 mt-2">
          The snippets above are pre-filled with the current app URL. When
          you deploy to a custom domain, come back to this page — the URL will
          auto-update to match. No manual edits required.
        </p>
      </section>
    </div>
  );
};

export default EmbedPage;
